
import { useMemo } from "react";
import PropTypes from "prop-types";

// react-chartjs-2 components
import "chart.js/auto";
import { Chart } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// PieChart configurations
import configs from "examples/Charts/PieChart/configs";

// ⬇️=========================================
// ⬇️ UPDATED COMPONENT
// ⬇️=========================================
function PieChart({
  icon = { color: "info", component: "" },
  title = "",
  description = "",
  height = "19.125rem",
  chart,
  onSliceClick = () => { },
  is_derived
}) {
  const { data, options } = configs(chart.labels || [], chart.datasets || {});

  // ✅ Memoize final options with click handler
  const finalOptions = useMemo(() => {
    const handleClick = (event, elements, chartInstance) => {
      // Check if a slice was clicked and the handler exists
      if (!onSliceClick || !elements || elements.length === 0) {
        return;
      }
      // Get the index of the clicked slice
      const { index } = elements[0];
      // Get the label (e.g., "video", "photo") from that index
      const label = chartInstance.data.labels[index];
      // Call the parent's handler
      onSliceClick(label);
    };

    return {
      ...options,
      onClick: handleClick,
    };
  }, [options, onSliceClick]); // Re-calculate if options or the callback changes

  const hasData = chart?.datasets?.totalEngagement > 0 || (chart?.datasets?.data && chart.datasets.data.some(val => val > 0));

  const renderChart = (
    <MDBox py={2} pr={2} pl={icon.component ? 1 : 2}>
      {title || description ? (
        <MDBox display="flex" px={description ? 1 : 0} pt={description ? 1 : 0}>
          {icon.component && (
            <MDBox
              width="4rem"
              height="4rem"
              bgColor={icon.color || "info"}
              variant="gradient"
              coloredShadow={icon.color || "info"}
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-5}
              mr={2}
            >
              <Icon fontSize="medium">{icon.component}</Icon>
            </MDBox>
          )}
          <MDBox mt={icon.component ? -2 : 0}>
            {title && <MDTypography variant="h6">{title}</MDTypography>}
            <MDBox mb={2}>
              <MDTypography component="div" variant="button" color="text">
                {description}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      ) : null}

      {hasData ? (
        useMemo(
          () => (
            <MDBox height={height}>
              <Chart
                type="pie"
                data={data}
                options={finalOptions} // ✅ Use final, merged options
                style={{ backgroundColor: "transparent", cursor: "pointer" }} // ✅ Add pointer
              />
            </MDBox>
          ),
          [data, height, finalOptions] // ✅ Use correct dependencies
        )
      ) : (
        <MDBox sx={{ p: "10px" }}>
          <MDTypography variant="subtitle1" color="text" align="center">
            No value found for the selected date range.
          </MDTypography>
        </MDBox>
      )}
    </MDBox>
  );

  return (
    title || description ? (
      <Card>
        {renderChart}
        {is_derived && (
          <MDBox p={1} display="flex" justifyContent="flex-end">
            <MDTypography
              variant="caption"
              color="text"
              sx={{
                fontStyle: "italic",
                fontSize: "0.7rem",
                opacity: 0.7,
                mr: 1
              }}
            >
              *Metric calculated by Comparedly
            </MDTypography>
          </MDBox>
        )}
      </Card>
    ) : renderChart
  );
}

// Typechecking props for the PieChart
PieChart.propTypes = {
  icon: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "light",
      "dark",
    ]),
    component: PropTypes.node,
  }),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
  onSliceClick: PropTypes.func, // ✅ Add prop type
};

export default PieChart;